import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, InlineError, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import {
  JourneyRefusal,
  journeyCommand,
  journeyOperationError,
  runJourneyCommand,
} from './journeyOperations';
import type { JourneyCommand, JourneyIntent } from './journeyOperations';
import {
  clearPendingJourney,
  pendingJourneyKey,
  readPendingJourney,
  savePendingJourney,
} from './journeyPending';

/** Multiple records execute sequentially, one atomic operation per record.
 * Previously completed operations are named and never retried as new requests.
 * This deliberately does not promise that an entire duplicate group is one
 * transaction. The failed command and every remaining UUID stay frozen. */
export function JourneyOperationDialog({
  intents,
  onClose,
  onSaved,
}: {
  intents: readonly JourneyIntent[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const [displayedIntents] = useState(intents);
  const actor = access.status === 'approved' ? access.profile.id : '';
  const currentActor = useRef(actor);
  currentActor.current = actor;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [pendingKey] = useState(() => pendingJourneyKey(actor, intents));
  const [recovery] = useState(() => {
    try {
      return { pending: readPendingJourney(pendingKey, intents), failed: false };
    } catch {
      return { pending: null, failed: true };
    }
  });
  const restored = recovery.pending;
  const [reason, setReason] = useState(restored?.commands[0]?.reason ?? '');
  const [commands, setCommands] = useState<readonly JourneyCommand[] | null>(
    restored?.commands ?? null,
  );
  const [completed, setCompleted] = useState(restored?.completed ?? 0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(
    recovery.failed ? 'Saved retry details could not be read. No new request was sent.' : '',
  );
  const merge = displayedIntents[0]?.kind === 'merge';
  const title = merge ? 'Merge duplicate leads safely' : 'Remove record from active use';
  async function save() {
    setError('');
    setPending(true);
    let confirmed = completed;
    try {
      if (recovery.failed) throw new Error('journey_pending_unavailable');
      if (!actor || pendingJourneyKey(actor, displayedIntents) !== pendingKey)
        throw new Error('journey_not_permitted');
      const frozen =
        commands ??
        displayedIntents.map((intent) =>
          journeyCommand(intent, reason, crypto.randomUUID(), actor),
        );
      savePendingJourney(pendingKey, frozen, completed);
      setCommands(frozen);
      for (let index = completed; index < frozen.length; index += 1) {
        const command = frozen[index];
        if (command === undefined) throw new Error('journey_operation_unconfirmed');
        if (!mounted.current || currentActor.current !== command.actorId)
          throw new Error('journey_not_permitted');
        await runJourneyCommand(client, command);
        if (!mounted.current || currentActor.current !== command.actorId)
          throw new Error('journey_not_permitted');
        savePendingJourney(pendingKey, frozen, index + 1);
        confirmed = index + 1;
        setCompleted(index + 1);
        // Refresh both drawers and reports even when a later pair refuses.
        await queryClient.invalidateQueries();
      }
      clearPendingJourney(pendingKey);
      onSaved();
    } catch (cause) {
      // Only a first attempt's definitive SQL refusal may be forgotten. A
      // refusal while checking an earlier lost response proves nothing about
      // the original commit, so retain that actor-bound receipt for recovery.
      if (commands === null && confirmed === 0 && cause instanceof JourneyRefusal)
        clearPendingJourney(pendingKey);
      if (mounted.current)
        setError(journeyOperationError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Dialog
      open
      title={title}
      onClose={() => {
        if (!pending) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" disabled={pending} onClick={onClose}>
            Close
          </Button>
          <Button
            variant="danger"
            disabled={pending || (!commands && !reason.trim()) || !displayedIntents.length}
            onClick={() => {
              void save();
            }}
          >
            {pending
              ? 'Saving…'
              : commands
                ? 'Retry same request'
                : merge
                  ? 'Confirm merge'
                  : 'Confirm removal'}
          </Button>
        </>
      }
    >
      <p>Evidence and earlier message versions remain in history. This does not erase them.</p>
      {merge && (
        <p>
          Each pair is one transaction. Consent wins. Cleared campaign boxes or conflicting
          histories refuse that pair without partial changes.
        </p>
      )}
      <ul>
        {displayedIntents.map((intent, index) => (
          <li key={index}>
            {intent.label}
            {index < completed ? ' · completed' : ''}
          </li>
        ))}
      </ul>
      {displayedIntents.length > 1 && (
        <p>
          {completed} of {displayedIntents.length} completed. Earlier completed actions stay
          completed if a later action is refused.
        </p>
      )}
      <Textarea
        label="Reason for this action"
        value={reason}
        disabled={pending || commands !== null}
        onChange={(event) => setReason(event.target.value)}
      />
      {error && <InlineError>{error}</InlineError>}
    </Dialog>
  );
}
